
import React, { useState, useMemo } from 'react';
import { ArrowLeft, Clock } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

interface Props {
  onBack: () => void;
}

export const InvestmentSimScreen: React.FC<Props> = ({ onBack }) => {
  const [monthlyInvest, setMonthlyInvest] = useState(500);
  const [years, setYears] = useState(30);
  const [rate, setRate] = useState(8);

  // Generate projection data
  const data = useMemo(() => {
    let balance = 0;
    let totalContributed = 0;
    const result = [];
    
    for (let i = 0; i <= years; i++) {
      result.push({
        year: 2024 + i,
        balance: Math.round(balance),
        contributed: totalContributed,
      });
      // Compound for next year
      balance = (balance + (monthlyInvest * 12)) * (1 + rate / 100);
      totalContributed += monthlyInvest * 12;
    }
    return result;
  }, [monthlyInvest, years, rate]);

  const finalAmount = data[data.length - 1].balance;
  const multiplier = (finalAmount / (data[data.length - 1].contributed || 1)).toFixed(1);

  return (
    <div className="max-w-4xl mx-auto px-4 pt-8 animate-fade-in pb-20">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors" aria-label="Back to Dashboard">
        <ArrowLeft size={20} />
        Back to Dashboard
      </button>

      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-4 bg-emerald/10 rounded-xl text-emerald">
            <Clock size={32} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white">The Time Machine</h1>
            <p className="text-slate-400 font-inter uppercase text-[10px] font-bold tracking-widest">Compound Interest Simulator</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Controls */}
          <div className="lg:col-span-1 space-y-8">
            <div>
              <label className="flex justify-between text-xs font-inter text-slate-400 uppercase mb-2 font-bold tracking-widest">
                <span>Monthly Investment</span>
                <span className="text-white">R{monthlyInvest}</span>
              </label>
              <input 
                type="range" 
                min="50" max="5000" step="50" 
                value={monthlyInvest}
                onChange={(e) => setMonthlyInvest(Number(e.target.value))}
                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald"
              />
            </div>

            <div>
              <label className="flex justify-between text-xs font-inter text-slate-400 uppercase mb-2 font-bold tracking-widest">
                <span>Time Horizon</span>
                <span className="text-white">{years} Years</span>
              </label>
              <input 
                type="range" 
                min="5" max="50" step="1" 
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald"
              />
            </div>

             <div>
              <label className="flex justify-between text-xs font-inter text-slate-400 uppercase mb-2 font-bold tracking-widest">
                <span>Rate of Return</span>
                <span className="text-white">{rate}%</span>
              </label>
              <input 
                type="range" 
                min="1" max="15" step="0.5" 
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald"
              />
            </div>

            <div className="p-4 bg-emerald/5 border border-emerald/10 rounded-xl">
               <div className="text-xs font-inter text-emerald uppercase mb-1 font-bold tracking-widest">Projected Wealth</div>
               <div className="text-2xl font-mono font-bold text-white">R{finalAmount.toLocaleString()}</div>
               <div className="text-xs font-inter text-slate-400 mt-2 font-bold tracking-tight">
                 Your money multiplied by <span className="text-white font-bold">{multiplier}x</span>
               </div>
            </div>
          </div>

          {/* Chart */}
          <div className="lg:col-span-2 h-80 bg-slate-900/50 rounded-xl p-4 border border-slate-700 w-full">
             <ResponsiveContainer width="100%" height="100%">
               <AreaChart data={data}>
                 <defs>
                   <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                     <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                     <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                   </linearGradient>
                 </defs>
                 <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                 <XAxis dataKey="year" stroke="#64748b" style={{ fontSize: '12px' }} />
                 <YAxis 
                    stroke="#64748b" 
                    tickFormatter={(val: number) => `R${val/1000}k`}
                    style={{ fontSize: '12px' }}
                 />
                 <Tooltip 
                   contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', color: '#fff' }}
                   formatter={(val: any) => [`R${val.toLocaleString()}`, 'Balance'] as any}
                 />
                 <Area 
                    type="monotone" 
                    dataKey="balance" 
                    stroke="#10B981" 
                    fillOpacity={1} 
                    fill="url(#colorBalance)" 
                    strokeWidth={3}
                 />
                 <Area 
                    type="monotone" 
                    dataKey="contributed" 
                    stroke="#475569" 
                    fill="transparent" 
                    strokeDasharray="5 5"
                    strokeWidth={2}
                 />
               </AreaChart>
             </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};
